import { useQuery } from "react-query";
import { organizationService } from "api/organization-administration";

import { useAuth } from "../../context";
import { resolveLogoPlacement, resolveLogoStyle } from "utils/logoStyle";

/**
 * Everything the app chrome needs to render an organisation's logo: the image, the alignment, and
 * which of the four logo slots are filled.
 *
 * One hook rather than three copies. `Sidebar`'s header, `Sidebar`'s footer and `Navbar` each used to
 * run this same query inline and re-derive the placement rules themselves, which is how the two sites
 * could disagree about whether the Antaia mark had moved. React Query already deduped the request via
 * the shared `["org", org_id]` key; what it could not dedupe was the reasoning.
 *
 * Layout state is not handled here — each caller ANDs in its own (`storedSidebarState` in the header,
 * a `useBreakpointValue` in the footer, nothing in the navbar).
 *
 * @returns {{logoSrc: ?string, location: "left"|"top", alignment: "center"|"left",
 *   showCustomerLogoInSidebar: boolean, showCustomerLogoInTopBar: boolean,
 *   showAntaiaInTopBar: boolean, showAntaiaBadgeInSidebarFooter: boolean}}
 */
export default function useOrgLogo() {
  const { user, getAccessTokenSilently } = useAuth();
  const org_id = user?.org_id;

  // Same key the OrgMgmt pages must invalidate after a save (they read the org under ["orgs", id]),
  // or the chrome keeps rendering the previous placement until something remounts it.
  const { data: orgData } = useQuery({
    queryKey: ["org", org_id],
    queryFn: () => getAccessTokenSilently().then(() => organizationService.get(org_id)),
    enabled: !!org_id,
  });

  const style = resolveLogoStyle(orgData);
  const logoSrc = resolveOrgLogoSrc(orgData);

  return { logoSrc, ...style, ...resolveLogoPlacement(style, !!logoSrc) };
}

/**
 * The org's logo image: the `logoUrl` an admin set, else a logo bundled under the org's subdomain,
 * else nothing.
 *
 * The bundled fallback exists to cover orgs never migrated to `logo_url` ("FALLBACK TO ACCOMODATE
 * LATE MIGRATION", as the original comment put it) and it had never once run: the old test was
 * `orgData?.logoUrl === null`, but `OrganizationInfoDto` is `@JsonInclude(NON_EMPTY)`, so a null
 * `logoUrl` is omitted from the response entirely and arrives as `undefined`. Those orgs rendered no
 * logo at all, and the 15 PNGs in assets/company-logos were dead weight. Hence a falsy check.
 *
 * Returning null when nothing resolves is a deliberate change from the old `catch`, which fell back to
 * the Antaia logo: that puts our mark where a customer's belongs, and on the dark sidebar it needs the
 * brightness/invert filter the footer applies to be legible at all. Callers render no image and keep
 * the spacer box, which is what gives the first nav row its gap from the top edge.
 */
function resolveOrgLogoSrc(orgData) {
  if (orgData?.logoUrl) return orgData.logoUrl;
  const subDomain = window.location.hostname.split(".")[0];
  try {
    // eslint-disable-next-line import/no-dynamic-require, global-require
    return require(`../../assets/company-logos/${subDomain}.png`);
  } catch (error) {
    // No bundled logo for this subdomain (the common case, and every localhost run).
    return null;
  }
}
